import { resolveHttpResourceUrl } from "../../../lib/http/resourceUrl";
import {
  BedDouble,
  MapPin,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import type { ItineraryCustomerHotel, PublicItinerary, PublicItineraryNode } from "../types";
import { itineraryPreviewImageUrl } from "../service/itineraryPreviewImageService";

const slotOrder = ["morning", "afternoon", "evening", "unscheduled"];

/** 客户行程唯一详情视图使用的原型头部。 */
export function ItineraryCustomerHeader({
  itinerary,
  internalActions,
  titleOnly = false,
}: {
  itinerary: PublicItinerary;
  internalActions?: ReactNode;
  titleOnly?: boolean;
}) {
  const [failedImageUrl, setFailedImageUrl] = useState<string>();
  const orderedNodes = orderedCustomerNodes(itinerary);
  const inspectionNodes = orderedNodes.filter(
    (node) => node.nodeType === "point" || node.nodeType === "custom",
  );
  const firstImageUrl = inspectionNodes[0]?.imageUrl?.trim() || undefined;
  const showImage = Boolean(firstImageUrl && firstImageUrl !== failedImageUrl);
  const accommodation = [...new Set(itinerary.days
    .map((day) => day.stayAreaName?.trim() ?? "")
    .filter(Boolean))]
    .join(" → ") || "住宿点待确认";

  return (
    <header className="itinerary-customer-header" role="region" aria-label="客户行程信息">
      {!titleOnly && showImage ? <div className="itinerary-customer-hero"><img
        src={firstImageUrl ? resolveHttpResourceUrl(itineraryPreviewImageUrl(firstImageUrl, "large")) : undefined}
        alt={`${itinerary.title || "客户行程"}封面`}
        loading="eager"
        decoding="async"
        onError={() => setFailedImageUrl(firstImageUrl)}
      /></div> : null}
      <div className="itinerary-customer-header-main">
        <h1>{itinerary.title || "客户行程"}</h1>
        {!titleOnly ? <dl className="itinerary-customer-meta">
          <div><dt>住宿点</dt><dd>{accommodation}</dd></div>
          <div><dt>团号</dt><dd>{itinerary.groupNumber?.trim() || "待确认"}</dd></div>
          <div><dt>时间</dt><dd>{dateRange(itinerary)}</dd></div>
        </dl> : null}
        {!titleOnly ? internalActions : null}
      </div>
    </header>
  );
}

/** 按确认件映射展示酒店，不输出采购和价格信息。 */
export function ItineraryHotelInfo({
  hotels,
  ariaLabel,
}: {
  hotels: ItineraryCustomerHotel[];
  ariaLabel: string;
}) {
  if (!hotels.length) return null;
  return (
    <section className="itinerary-customer-hotels" role="region" aria-label={ariaLabel}>
      <h3><BedDouble size={17} aria-hidden="true" />酒店信息</h3>
      <div className="itinerary-customer-hotel-list">
        {hotels.map((hotel, hotelIndex) => <article
          className="itinerary-customer-hotel"
          key={`${hotel.hotelName}-${hotel.dayNumbers.join("-")}-${hotelIndex}`}
        >
          <div className="itinerary-customer-hotel-heading">
            <strong>{hotel.hotelName}</strong>
            {hotel.address ? <span><MapPin size={13} aria-hidden="true" />{hotel.address}</span> : null}
          </div>
          {hotel.rooms.length ? <ul>{hotel.rooms.map((room, roomIndex) => <li key={`${room.roomName}-${roomIndex}`}>
            <span>{room.roomName}</span>
            <small>{room.quantity} {room.unit} × {room.billingCount} 次</small>
          </li>)}</ul> : null}
        </article>)}
      </div>
    </section>
  );
}

/** 按日次、时段和节点顺序返回客户可见节点。 */
function orderedCustomerNodes(itinerary: PublicItinerary): PublicItineraryNode[] {
  return itinerary.days.flatMap((day) => [
    ...day.nodes,
    ...(day.parallelTracks ?? []).flatMap((track) => track.nodes),
  ].sort((left, right) => slotOrder.indexOf(left.timeSlot) - slotOrder.indexOf(right.timeSlot)
      || left.sortOrder - right.sortOrder));
}

/** 生成客户头部使用的日期范围。 */
function dateRange(itinerary: PublicItinerary): string {
  if (itinerary.startDate && itinerary.endDate) return `${itinerary.startDate} 至 ${itinerary.endDate}`;
  return itinerary.startDate || itinerary.endDate || "日期待定";
}
